import { TableCellTypes, TypographyColors } from '@carboledger/ui';
import { CellText, makeUnitTypeCell, makeUnitScopedCell } from '@/components/common';
import { useTranslations } from '@/hooks';
import { ALLOCATION_STATUS_TONE } from '@/config/statusTones';
import { buildAllocationStatusVariants } from '@/config/statuses';
import { buildRowUnitOptions, unitTypeLabel } from './orderUnits';

// Order details ▸ Allocation details tab — columns.

// Below which "Days left" turns red — matches the allocations list's own URGENT_DAYS.
const URGENT_DAYS = 7;

const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderCertifiedQuantity = makeUnitScopedCell('certifiedQuantity');
const DaysLeftCell = ({ value }) => {
  const t = useTranslations();
  if (value == null) return <CellText color={TypographyColors.secondary}>-</CellText>;
  const color = value < URGENT_DAYS ? TypographyColors.error : TypographyColors.primary;
  return <CellText color={color}>{t('ALLOCATIONS_DAYS_LEFT', { days: value })}</CellText>;
};
const renderDaysLeft = (ctx) => <DaysLeftCell value={ctx.value} />;

// A stub for now — no view-declaration flow exists yet, so it's a label, not a link.
const DeclarationLinkCell = ({ row }) => {
  const t = useTranslations();
  if (row.associatedDeclarationNo === '-')
    return <CellText color={TypographyColors.secondary}>-</CellText>;
  return <CellText color={TypographyColors.brand}>{t('COMMON_VIEW')}</CellText>;
};
const renderDeclarationLink = (ctx) => <DeclarationLinkCell row={ctx.row} />;

const textColumn = (id, header, width = 200) => ({ id, header, type: TableCellTypes.text, width });

export const buildOrderAllocationColumns = (t, { unitHeaderSelect }) => [
  { ...textColumn('transactionId', t('ALLOCATIONS_COL_TRANSACTION_ID'), 160), pinnable: true },
  {
    id: 'status',
    header: t('FIELD_STATUS'),
    type: TableCellTypes.statusDot,
    width: 180,
    variantMap: buildAllocationStatusVariants(t, ALLOCATION_STATUS_TONE),
  },
  textColumn('sendTo', t('ALLOCATIONS_COL_SEND_TO'), 192),
  textColumn('shippingTo', t('ALLOCATIONS_COL_SHIPPING_TO'), 192),
  textColumn('transactionDate', t('ORDERS_DETAIL_COL_ALLOCATED_DATE'), 150),
  {
    id: 'unitType',
    header: t('FIELD_UNIT_TYPE', { unit: unitTypeLabel(t, unitHeaderSelect.value) }),
    type: TableCellTypes.custom,
    width: 252,
    cell: renderUnitTypeCell,
    headerSelect: unitHeaderSelect,
  },
  {
    id: 'certifiedQuantity',
    header: t('ALLOCATIONS_COL_CERTIFIED_QUANTITY'),
    type: TableCellTypes.custom,
    width: 200,
    cell: renderCertifiedQuantity,
  },
  textColumn('shippingToSites', t('FIELD_SHIPPING_TO'), 192),
  textColumn('massBalancePeriod', t('ALLOCATIONS_COL_MASS_BALANCE_PERIOD'), 260),
  textColumn('reference', t('ALLOCATIONS_COL_REFERENCE'), 180),
  textColumn('associatedDeclarationNo', t('ALLOCATIONS_COL_ASSOCIATED_DECLARATION_NO'), 220),
  {
    id: 'declarationLink',
    header: t('ORDERS_DETAIL_COL_DECLARATIONS'),
    type: TableCellTypes.custom,
    width: 140,
    cell: renderDeclarationLink,
  },
  textColumn('transactedBy', t('ALLOCATIONS_COL_TRANSACTED_BY'), 200),
  textColumn('countryOfOrigin', t('FIELD_COUNTRY_OF_ORIGIN')),
  textColumn('rawMaterialType', t('FIELD_RAW_MATERIAL_TYPE')),
  textColumn('ghgEmissions', t('FIELD_GHG_EMISSIONS')),
  textColumn('ghgEmissionSavings', t('ALLOCATIONS_COL_GHG_EMISSION_SAVINGS')),
  textColumn('feedstockCategory', t('FIELD_FEEDSTOCK_CATEGORY')),
  textColumn('allocatedQuota', t('ALLOCATIONS_COL_ALLOCATED_QUOTA')),
  textColumn('dateOfDispatch', t('FIELD_DATE_OF_DISPATCH'), 150),
  {
    id: 'daysLeft',
    header: t('FIELD_DAYS_LEFT'),
    type: TableCellTypes.custom,
    width: 150,
    cell: renderDaysLeft,
  },
];
